import React from 'react';
import PropTypes from 'prop-types';

import FileUpload from '../../file-upload/file-upload';
import FileListItem from '../../file-upload/file-list-item';
import LoadMore from '../../../nucleus/load-more/load-more';
import companiesService, { defaultCompanyData } from '../../../services/companies-service';

import vouchersService from '../../../services/voucher/vouchers-service';
import dealsService from '../../../services/deals-service';
import * as compDataService from '../../../services/voucher/comp-data-service';

class FilesTab extends React.Component {
  state = {
    queryParams: {
      ordering: '-modified',
      page: 1,
      page_size: 25,
    },
  }

  leaseArtifactsInitializedForDealId = null;

  componentDidMount() {
    const { queryParams } = this.state;
    const { dealId, fetchFiles } = this.props;
    fetchFiles({ ...queryParams, deal: dealId });
    this.initializeLeaseArtifacts();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.deal !== this.props.deal) {
      this.initializeLeaseArtifacts();
    }
  }

  initializeLeaseArtifacts = () => {
    const { deal, dealId } = this.props;

    if (!deal || !deal.id || this.leaseArtifactsInitializedForDealId === deal.id) {
      return;
    }

    if (deal.deal_type !== 'lease') {
      this.leaseArtifactsInitializedForDealId = deal.id;
      return;
    }

    this.leaseArtifactsInitializedForDealId = deal.id;
    const promises = [];
    const voucherCommercialProperties = Array.isArray(deal.voucher_commercial_properties)
      ? deal.voucher_commercial_properties
      : [];

    if (deal.counter_party === null) {
      const createCompanyPromise = companiesService.createCompany({ ...defaultCompanyData })
        .then(res => dealsService.updateDeal(dealId, { counter_party: res.id }));
      promises.push(createCompanyPromise);
    }

    if (deal.client === null) {
      const createClientPromise = companiesService.createCompany({ ...defaultCompanyData })
        .then(res => dealsService.updateDeal(dealId, { client: res.id }));
      promises.push(createClientPromise);
    }

    if (deal.voucher === null) {
      const createVoucherPromise = vouchersService.createVoucher(dealId);
      promises.push(createVoucherPromise);
    }

    if (voucherCommercialProperties.length === 0) {
      const createCommercialPropertyPromise = vouchersService.createCommercialProperty({
        deal: dealId,
      });
      promises.push(createCommercialPropertyPromise);
    }

    if (deal.internalcomp === null) {
      const createInternalCompPromise = compDataService.createCompData({
        deal: dealId,
        [`${deal.rep_role}_represented_by`]: 'CBRE',
        ...(deal.deal_type !== 'sale' && {
          rent_escalation_is_custom: false,
        }),
      });
      promises.push(createInternalCompPromise);
    }

    Promise.all(promises)
      .catch(() => console.error('Error initializing lease artifacts in Files tab'));
  }

  handleLoadMore = () => {
    const { queryParams } = this.state;
    const { dealId, fetchFiles } = this.props;
    this.setState({
      queryParams: {
        ...queryParams,
        page: queryParams.page += 1,
      },
    }, () => fetchFiles({ ...queryParams, deal: dealId }));
  }

  render() {
    const {
      analyticProperties,
      dealId,
      dealType,
      isEnhancedFlow,
      deleteFile,
      fetchFilesDownloadLink,
      files,
      isLoadingFiles,
      next,
      updateFile,
      uploadFile,
      userData,
    } = this.props;

    return (
      <div className="files-tab detail-panels-layout">
        <div className="col-two-thirds card-default">
          <FileUpload
            analyticProperties={analyticProperties}
            dealId={dealId}
            dealType={dealType}
            isEnhancedFlow={isEnhancedFlow}
            deleteFile={deleteFile}
            fetchFilesDownloadLink={fetchFilesDownloadLink}
            isLoadingFiles={isLoadingFiles}
            uploadFile={uploadFile}
          />
          <ul className="files">
            {files &&
              files.map(file => (
                <FileListItem
                  analyticProperties={analyticProperties}
                  dealType={dealType}
                  isEnhancedFlow={isEnhancedFlow}
                  deleteFile={deleteFile}
                  fetchFilesDownloadLink={fetchFilesDownloadLink}
                  file={file}
                  key={file.id}
                  updateFile={updateFile}
                  userData={userData}
                />
              ))
            }
          </ul>
          {files.length >= 25 &&
            <LoadMore
              isLoading={isLoadingFiles}
              handleLoadMore={this.handleLoadMore}
              hasNextPage={next}
            />
          }
        </div>
      </div>
    );
  }
}

FilesTab.propTypes = {
  analyticProperties: PropTypes.shape({
    actionPrefix: PropTypes.string,
    categoryPrefix: PropTypes.string,
  }).isRequired,
  deal: PropTypes.shape({
    id: PropTypes.number,
    deal_type: PropTypes.string,
    counter_party: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    client: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    voucher: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    voucher_commercial_properties: PropTypes.arrayOf(PropTypes.shape({})),
    internalcomp: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    rep_role: PropTypes.string,
  }),
  dealId: PropTypes.number.isRequired,
  dealType: PropTypes.string,
  deleteFile: PropTypes.func.isRequired,
  fetchFiles: PropTypes.func.isRequired,
  fetchFilesDownloadLink: PropTypes.func.isRequired,
  files: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    deal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    document_name: PropTypes.string,
    title: PropTypes.string,
    file_type: PropTypes.string,
    file_size: PropTypes.number,
    created_by: PropTypes.shape({
      id: PropTypes.number,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      avatar: PropTypes.string,
      email: PropTypes.shape({
        work: PropTypes.string,
      }),
    }),
    created: PropTypes.string,
    modified: PropTypes.string,
    can_edit: PropTypes.bool,
    privacy: PropTypes.string,
    is_lease_document: PropTypes.bool,
  })).isRequired,
  isLoadingFiles: PropTypes.bool,
  next: PropTypes.number,
  updateFile: PropTypes.func,
  uploadFile: PropTypes.func.isRequired,
  userData: PropTypes.shape({}).isRequired,
};

FilesTab.defaultProps = {
  deal: null,
  dealType: null,
  isLoadingFiles: false,
  next: null,
  updateFile: () => {},
};

export default FilesTab;
